import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { ContentQueryParams, Folder, SubCategoryType } from '../../../data/types/content.interface';
import {
  FormControl,
  Button,
  Popper,
  ClickAwayListener,
  Grid2,
  Paper,
  Autocomplete,
  Stack,
  TextField,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Divider,
  InputAdornment,
  Chip,
} from '@mui/material';
import { TagType } from '../../../data/types/tags.interface';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CalendarToday } from '@mui/icons-material';
import { MarketingPlatforms } from '../constants';
import { CloseIcon, DownIcon, UpIcon } from './AppIcons';

const useStyles = makeStyles({
  contentSection: {
    padding: '17px 17px 14px 17px',
    width: '514px',
  },
  cancel: {
    color: '#F67570 !important',
    marginRight: '24px !important',
    fontSize: '16px !important',
    fontFamily: 'Lato !important',
  },
  save: {
    width: '127px',
    height: '40px',
    fontSize: '18px !important',
    backgroundColor: '#F67570 !important',
    boxShadow: 'none !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
  },
  toggleButtonContainer: {
    '& label': {
      marginLeft: 0,
    },
    '& span.MuiSwitch-root': {
      marginTop: '-12px',
    },
  },
  bold: {
    fontFamily: 'Lato !important',
    color: 'rgba(0, 0, 0, 0.87) !important',
  },
  formControlLabels: {
    '& .MuiTypography-root': {
      fontFamily: 'Lato !important',
    },
  },
  textField: {
    '& .MuiInputBase-root': {
      border: '1px solid #E1E3E8',
      borderRadius: '4px',
      padding: '4px 8px',
      backgroundColor: '#fff',
      height: 'auto',
      minHeight: '40px',
      marginBottom: '4px',
    },
    '& .MuiInputBase-input': {
      padding: '6px 0',
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingRight: '0 !important',
    },
    '& .MuiInput-underline:before': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:after': {
      borderBottom: 'none',
    },
    '& .MuiInput-underline:hover:not(.Mui-disabled, .Mui-error):before': {
      borderBottom: 'none',
    },
    '& Chip': {
      border: '1px solid ##E1E3E8',
      color: '#5F6774',
      backgroundColor: '#FAFAFA',
    },
  },
  radioBtn: {
    '& span': {
      '& svg': {
        fill: '#E1E3E8 !important',
      },
    },
    '& .Mui-checked': {
      '& svg': {
        fill: '#30343F !important',
      },
    },
  },
  dateField: {
    fontFamily: 'Lato !important',
    '& .react-datepicker-wrapper': {
      padding: '0 8px 0 0',
      width: '49%',
      height: '40px',
      '& .MuiFormControl-root-MuiTextField-root': {
        height: '40px',
      },
      '& input': {
        height: '8px !important',
      },
    },
    '& .react-datepicker-popper': {
      left: '-36px !important',
      '& .react-datepicker__triangle': {
        marginLeft: '27px',
      },
    },
  },
  dropdownIcon: {
    fontSize: '42px !important',
    '& path': {
      fill: '#5F6774 !important',
    },
  },
});

type Props = {
  open: boolean;
  anchorEl: null | HTMLElement;
  handleClose: () => void;
  handleFilters: (filters: ContentQueryParams) => void;
  list: Folder[];
  categories: SubCategoryType[];
  subCategories: SubCategoryType[];
  contentTypes: TagType[];
  fileType: string;
  themeList: TagType[];
  topicList: TagType[];
};

const FilterModalForContent: React.FC<Props> = ({
  handleFilters,
  open,
  anchorEl,
  list,
  categories,
  subCategories,
  contentTypes,
  fileType,
  themeList,
  topicList,
  handleClose,
}) => {
  const classes = useStyles();
  const [selectedFolder, setSelectedFolder] = React.useState<Folder[]>([]);
  const [selectedCategory, setSelectedCategory] = React.useState<SubCategoryType[]>([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategoryType[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedType, setSelectedType] = useState<TagType[]>([]);
  const [usedOrUnused, setUsedOrUnused] = useState<string | undefined>(undefined);
  const [videoType, setVideoType] = React.useState<string>('');
  const [selectedThemes, setSelectedThemes] = React.useState<TagType[]>([]);
  const [selectedTopics, setSelectedTopics] = React.useState<TagType[]>([]);
  const [platform, setPlatform] = React.useState<TagType[]>([]);
  const [publishStatus, setPublishStatus] = useState<string>('');
  const [openDropDown, setOpenDropDown] = useState(false);

  const defaultFolderProps = {
    options: list,
    getOptionLabel: (option: Folder) => option.name,
  };
  const defaultCategoryProps = {
    options: categories,
    getOptionLabel: (option: SubCategoryType) => option.name,
  };
  const defaultSubCategoryProps = {
    options: subCategories,
    getOptionLabel: (option: SubCategoryType) => option.name,
  };
  const defaultProps = {
    options: contentTypes,
    getOptionLabel: (option: TagType) => option.name,
  };
  const defaultThemeProps = {
    options: themeList,
    getOptionLabel: (option: TagType) => option.name,
  };
  const defaultTopicProps = {
    options: topicList.filter((topic) => selectedThemes.map((theme) => theme.id).includes(topic.parentThemeId || 0)),
    getOptionLabel: (option: TagType) => option.name,
  };
  const defaultPlatformProps = {
    options: MarketingPlatforms,
    getOptionLabel: (option: TagType) => option.name,
  };

  const handleFolderChange = (e: React.SyntheticEvent<Element, Event>, values: Folder[]) => {
    setSelectedFolder(values);
  };
  const handleCategoryChange = (e: React.SyntheticEvent<Element, Event>, values: SubCategoryType[]) => {
    setSelectedCategory(values);
  };
  const handleSubCategoryChange = (e: React.SyntheticEvent<Element, Event>, values: SubCategoryType[]) => {
    setSelectedSubCategory(values);
  };
  const handleAssetTypeChange = (e: React.SyntheticEvent<Element, Event>, values: TagType[]) => {
    setSelectedType(values);
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsedOrUnused((event.target as HTMLInputElement).value);
  };
  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVideoType((event.target as HTMLInputElement).value);
  };
  const handleThemeChange = (e: React.SyntheticEvent<Element, Event>, values: TagType[]) => {
    setSelectedThemes(values);
  };
  const handleTopicChange = (e: React.SyntheticEvent<Element, Event>, values: TagType[]) => {
    setSelectedTopics(values);
  };
  const handlePlatformChange = (e: React.SyntheticEvent<Element, Event>, values: TagType[]) => {
    setPlatform(values);
  };
  const handlePublishStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPublishStatus((event.target as HTMLInputElement).value);
  };

  const handleApply = () => {
    handleFilters({
      folder: selectedFolder,
      category: selectedCategory,
      subCategory: selectedSubCategory,
      startDate,
      endDate,
      assetType: selectedType,
      usedOrUnused,
      videoType,
      themes: selectedThemes,
      topics: selectedTopics,
      platform,
      publishStatus,
      perPage: 50,
      page: 1,
    });
  };

  const handleCancel = () => {
    setSelectedFolder([]);
    setSelectedCategory([]);
    setSelectedSubCategory([]);
    setStartDate(null);
    setEndDate(null);
    setSelectedType([]);
    setUsedOrUnused(undefined);
    setVideoType('');
    setSelectedThemes([]);
    setSelectedTopics([]);
    setPlatform([]);
    setPublishStatus('');
    handleClose();
  };

  return (
    <Popper
      open={open}
      aria-modal="true"
      role="dialog"
      anchorEl={anchorEl}
      placement="bottom-end"
      style={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <Paper variant="elevation" className={classes.contentSection}>
          <Grid2 container direction="column">
            <Grid2 container fontWeight="bold">
              <span>Filter By</span>
            </Grid2>

            <Grid2 mt={2}>
              <Divider
                orientation="horizontal"
                flexItem
                sx={{
                  backgroundColor: '#E1E3E8',
                  paddingBottom: '1px',
                }}
              />
            </Grid2>

            <Grid2 container direction="row" mt={2}>
              <label>Main Folder</label>
              <Stack spacing={1} sx={{ width: '100%' }} className={classes.textField}>
                <Autocomplete
                  {...defaultFolderProps}
                  id="auto-highlight"
                  autoHighlight
                  multiple
                  value={selectedFolder}
                  onChange={handleFolderChange}
                  renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                  onOpen={() => setOpenDropDown(true)}
                  onClose={() => setOpenDropDown(false)}
                  popupIcon={
                    openDropDown ? (
                      <UpIcon className={classes.dropdownIcon} />
                    ) : (
                      <DownIcon className={classes.dropdownIcon} />
                    )
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          variant="outlined"
                          label={option.name}
                          sx={{ height: '21px', color: '#5F6774', fontFamily: 'Lato', border: '1px solid #E1E3E8' }}
                          size="small"
                          deleteIcon={<CloseIcon viewBox="5 7 27 27" sx={{ width: '22px', height: '22px' }} />}
                          {...tagProps}
                        />
                      );
                    })
                  }
                />
              </Stack>
            </Grid2>

            <Grid2 container direction="row" mt={2} minHeight={76} flexWrap="nowrap">
              <Grid2 container direction="column" sx={{ width: '49%' }}>
                <label>Category</label>
                <Stack spacing={1} className={classes.textField}>
                  <Autocomplete
                    {...defaultCategoryProps}
                    id="auto-highlight"
                    autoHighlight
                    multiple
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                    onOpen={() => setOpenDropDown(true)}
                    onClose={() => setOpenDropDown(false)}
                    popupIcon={
                      openDropDown ? (
                        <UpIcon className={classes.dropdownIcon} />
                      ) : (
                        <DownIcon className={classes.dropdownIcon} />
                      )
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            variant="outlined"
                            label={option.name}
                            size="small"
                            sx={{ height: '21px', color: '#5F6774', fontFamily: 'Lato', border: '1px solid #E1E3E8' }}
                            deleteIcon={<CloseIcon viewBox="5 7 27 27" sx={{ width: '22px', height: '22px' }} />}
                            {...tagProps}
                          />
                        );
                      })
                    }
                  />
                </Stack>
              </Grid2>

              <Grid2 container direction="column" ml={1} sx={{ width: '49%' }}>
                <label>Sub Category</label>
                <Stack spacing={1} className={classes.textField}>
                  <Autocomplete
                    {...defaultSubCategoryProps}
                    id="auto-highlight"
                    autoHighlight
                    multiple
                    value={selectedSubCategory}
                    onChange={handleSubCategoryChange}
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                    onOpen={() => setOpenDropDown(true)}
                    onClose={() => setOpenDropDown(false)}
                    popupIcon={
                      openDropDown ? (
                        <UpIcon className={classes.dropdownIcon} />
                      ) : (
                        <DownIcon className={classes.dropdownIcon} />
                      )
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            variant="outlined"
                            label={option.name}
                            size="small"
                            sx={{ height: '21px', color: '#5F6774', fontFamily: 'Lato', border: '1px solid #E1E3E8' }}
                            deleteIcon={<CloseIcon viewBox="5 7 27 27" sx={{ width: '22px', height: '22px' }} />}
                            {...tagProps}
                          />
                        );
                      })
                    }
                  />
                </Stack>
              </Grid2>
            </Grid2>

            <Grid2 container direction="column" width="101%">
              <label>{fileType === 'marketing' ? 'Published At' : 'Date Added'} </label>
              <Grid2 container className={classes.dateField} width="104%">
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="MM/dd/yyyy"
                  customInput={
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="MM/DD/YYYY"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarToday fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  }
                />
                <DatePicker
                  selected={endDate}
                  onChange={(date: Date) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="MM/dd/yyyy"
                  customInput={
                    <TextField
                      variant="outlined"
                      fullWidth
                      placeholder="MM/DD/YYYY"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarToday fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  }
                />
              </Grid2>
            </Grid2>

            <Grid2 container mt={4} minHeight={76} flexWrap="nowrap">
              <Grid2 container direction="column" sx={{ width: contentTypes.length > 0 ? '49%' : '100%' }}>
                <label>Theme</label>
                <Stack spacing={1} className={classes.textField}>
                  <Autocomplete
                    {...defaultThemeProps}
                    id="auto-highlight"
                    autoHighlight
                    multiple
                    value={selectedThemes}
                    onChange={handleThemeChange}
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                    onOpen={() => setOpenDropDown(true)}
                    onClose={() => setOpenDropDown(false)}
                    popupIcon={
                      openDropDown ? (
                        <UpIcon className={classes.dropdownIcon} />
                      ) : (
                        <DownIcon className={classes.dropdownIcon} />
                      )
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            variant="outlined"
                            label={option.name}
                            size="small"
                            sx={{ height: '21px', color: '#5F6774', fontFamily: 'Lato', border: '1px solid #E1E3E8' }}
                            deleteIcon={<CloseIcon viewBox="5 7 27 27" sx={{ width: '22px', height: '22px' }} />}
                            {...tagProps}
                          />
                        );
                      })
                    }
                  />
                </Stack>
              </Grid2>

              {contentTypes.length > 0 && (
                <Grid2 container direction="column" ml={1} sx={{ width: '49%' }}>
                  <label>Content Types</label>
                  <Stack spacing={1} className={classes.textField}>
                    <Autocomplete
                      {...defaultProps}
                      id="auto-highlight"
                      autoHighlight
                      multiple
                      value={selectedType}
                      onChange={handleAssetTypeChange}
                      renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                      onOpen={() => setOpenDropDown(true)}
                      onClose={() => setOpenDropDown(false)}
                      popupIcon={
                        openDropDown ? (
                          <UpIcon className={classes.dropdownIcon} />
                        ) : (
                          <DownIcon className={classes.dropdownIcon} />
                        )
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              variant="outlined"
                              label={option.name}
                              size="small"
                              sx={{ height: '21px', color: '#5F6774', fontFamily: 'Lato', border: '1px solid #E1E3E8' }}
                              deleteIcon={<CloseIcon viewBox="5 7 27 27" sx={{ width: '22px', height: '22px' }} />}
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Stack>
                </Grid2>
              )}
            </Grid2>

            {fileType !== 'marketing' && (
              <Grid2 container flexWrap="nowrap">
                <Grid2 container sx={{ width: '49%' }} className={classes.toggleButtonContainer}>
                  <FormControl className={classes.formControlLabels}>
                    <FormLabel id="demo-controlled-radio-buttons-group" className={classes.bold}>
                      Used/Unused
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="demo-controlled-radio-buttons-group"
                      name="controlled-radio-buttons-group"
                      value={usedOrUnused}
                      onChange={handleChange}
                    >
                      <Grid2 container direction="column">
                        <FormControlLabel className={classes.radioBtn} value="Used" control={<Radio />} label="Used" />
                        <FormControlLabel
                          className={classes.radioBtn}
                          value="Unused"
                          control={<Radio />}
                          label="Not Used"
                        />
                        <FormControlLabel
                          className={classes.radioBtn}
                          value="NotInUse"
                          control={<Radio />}
                          label="Not In Use"
                        />
                      </Grid2>
                    </RadioGroup>
                  </FormControl>
                </Grid2>

                <Grid2 container direction="column" ml={1} sx={{ width: '49%' }}>
                  <FormControl className={classes.formControlLabels}>
                    <FormLabel id="demo-controlled-radio-buttons-group" className={classes.bold}>
                      Video Type
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="demo-controlled-radio-buttons-group"
                      name="controlled-radio-buttons-group"
                      value={videoType}
                      onChange={handleTypeChange}
                    >
                      <Grid2 container direction="column">
                        {fileType !== 'raw' ? (
                          <>
                            <Tooltip title="Original video clip">
                              <FormControlLabel
                                value="long_original"
                                className={classes.radioBtn}
                                control={<Radio />}
                                label="Long Original"
                              />
                            </Tooltip>
                            <Tooltip title="short videos made out of original clip">
                              <FormControlLabel
                                value="long_chopped"
                                className={classes.radioBtn}
                                control={<Radio />}
                                label="Long Chopped"
                              />
                            </Tooltip>
                          </>
                        ) : (
                          <Tooltip title=" Long clip">
                            <FormControlLabel
                              value="long"
                              className={classes.radioBtn}
                              control={<Radio />}
                              label="Long"
                            />
                          </Tooltip>
                        )}
                        <Tooltip title="short clip">
                          <FormControlLabel
                            value="short"
                            className={classes.radioBtn}
                            control={<Radio />}
                            label="Short"
                          />
                        </Tooltip>
                      </Grid2>
                    </RadioGroup>
                  </FormControl>
                </Grid2>
              </Grid2>
            )}

            <Grid2 container mt={2} direction="row" mb={1}>
              {(fileType === 'preFinal' || fileType === 'final') && (
                <Grid2 container direction="column" sx={{ width: '49%' }}>
                  <label>Topic</label>
                  <Stack spacing={1} className={classes.textField}>
                    <Autocomplete
                      {...defaultTopicProps}
                      id="auto-highlight"
                      multiple
                      autoHighlight
                      value={selectedTopics}
                      onChange={handleTopicChange}
                      renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                      onOpen={() => setOpenDropDown(true)}
                      onClose={() => setOpenDropDown(false)}
                      popupIcon={
                        openDropDown ? (
                          <UpIcon className={classes.dropdownIcon} />
                        ) : (
                          <DownIcon className={classes.dropdownIcon} />
                        )
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              variant="outlined"
                              label={option.name}
                              size="small"
                              sx={{ height: '21px', color: '#5F6774', fontFamily: 'Lato', border: '1px solid #E1E3E8' }}
                              deleteIcon={<CloseIcon viewBox="5 7 27 27" sx={{ width: '22px', height: '22px' }} />}
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Stack>
                </Grid2>
              )}
            </Grid2>

            {fileType === 'marketing' && (
              <Grid2 container>
                <Grid2 container direction="column" sx={{ width: '49%' }}>
                  <label>Marketing Platform</label>
                  <Stack spacing={1} className={classes.textField}>
                    <Autocomplete
                      {...defaultPlatformProps}
                      id="auto-highlight"
                      autoHighlight
                      multiple
                      value={platform}
                      onChange={handlePlatformChange}
                      renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                      onOpen={() => setOpenDropDown(true)}
                      onClose={() => setOpenDropDown(false)}
                      popupIcon={
                        openDropDown ? (
                          <UpIcon className={classes.dropdownIcon} />
                        ) : (
                          <DownIcon className={classes.dropdownIcon} />
                        )
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              deleteIcon={<CloseIcon viewBox="5 7 27 27" sx={{ width: '22px', height: '22px' }} />}
                              variant="outlined"
                              label={option.name}
                              sx={{ height: '21px', color: '#5F6774', fontFamily: 'Lato', border: '1px solid #E1E3E8' }}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Stack>
                </Grid2>
                <Grid2 container ml={2} className={classes.toggleButtonContainer}>
                  <FormControl className={classes.formControlLabels}>
                    <FormLabel id="demo-controlled-radio-buttons-group" className={classes.bold}>
                      Published Status
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="demo-controlled-radio-buttons-group"
                      name="controlled-radio-buttons-group"
                      value={publishStatus}
                      onChange={handlePublishStatusChange}
                    >
                      <Grid2 container direction="column">
                        <FormControlLabel
                          value="published"
                          className={classes.radioBtn}
                          control={<Radio />}
                          label="Published"
                        />
                        <FormControlLabel
                          value="unPublished"
                          className={classes.radioBtn}
                          control={<Radio />}
                          label="Not Published"
                        />
                        <FormControlLabel
                          value="scheduled"
                          className={classes.radioBtn}
                          control={<Radio />}
                          label="Scheduled"
                        />
                      </Grid2>
                    </RadioGroup>
                  </FormControl>
                </Grid2>
              </Grid2>
            )}

            <Grid2>
              <Divider
                orientation="horizontal"
                flexItem
                sx={{
                  backgroundColor: '#E1E3E8',
                  paddingBottom: '1px',
                }}
              />
            </Grid2>

            <Grid2 container mt={2} justifyContent="end">
              <Button size="small" variant="text" type="button" className={classes.cancel} onClick={handleCancel}>
                cancel
              </Button>
              <Button size="small" className={classes.save} variant="contained" onClick={handleApply}>
                <span style={{ fontSize: 'h5', textTransform: 'none' }}>Apply Filters</span>
              </Button>
            </Grid2>
          </Grid2>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default FilterModalForContent;
